import React, { useState, useEffect, useRef } from 'react';
import { Spinner, Button, ProgressBar, Dropdown } from "react-bootstrap";
import axios from 'axios';
import { useGamepads } from 'react-gamepads';
import './Fpv.css';
import FpvStat from './FpvStat';
import { logger } from '../../../utils/logger';
import { MavlinkParser } from '../../../utils/MavlinkParser';
import { MavMessage } from '../../../utils/MavMessage';
import { BatteryStatusMessage, HeartbeatMessage, VfrHudMessage } from '../../../utils/MavMessageTypes';
import TelemetryStat from './TelemetryStat';

interface FpvRtcProps {
    drone: Drone | null;
    /** Выйти из FPV обратно в консоль. */
    onClose: () => void;
}

const FpvRtc: React.FC<FpvRtcProps> = ({ drone, onClose }) => {
    const [pc, setPc] = useState<RTCPeerConnection | null>(null);
    const pcRef = useRef<RTCPeerConnection | null>(null);
    const websocketRef = useRef<WebSocket | null>(null);
    const reconnectingRef = useRef<boolean>(false);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const videoContainerRef = useRef<HTMLDivElement | null>(null);
    const sendingWsRequestRef = useRef<NodeJS.Timeout | null>(null);
    const sendingPing = useRef<boolean>(false);
    // Выходим из FPV: закрытие WebSocket больше не должно запускать переподключение.
    const closingRef = useRef<boolean>(false);

    const [showPlayButton, setShowPlayButton] = useState(true);
    const [waitVideo, setWaitVideo] = useState(false);
    const [ice, setIce] = useState<IceConfig | null>(null);

    const gamepadRef = useRef<(Gamepad | null)[]>([]);
    const [gamepadId, setGamepadId] = useState<number>(0);

    const rcDataChannelRef = useRef<RTCDataChannel | null>(null);
    const telemDataChannelRef = useRef<RTCDataChannel | null>(null);
    const bitrateDataChannelRef = useRef<RTCDataChannel | null>(null);

    const [axisReverse, setAxisReverse] = useState<number[]>([0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
    const [axisOrder, setAxisOrder] = useState<number[]>([0, 1, 2, 3, 4, 5, 6, 7, 8, 9]);

    const [loadingText, setLoadingText] = useState(" Загрузка...");
    const [axesValues, setAxesValues] = useState<number[]>(Array(10).fill(0));
    const axisOrderRef = useRef(axisOrder);
    const axisReverceRef = useRef(axisOrder);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [p2pSettingsOpen, setP2PSettingsOpen] = useState(false);
    const [connectionType, setConnectionType] = useState("...");
    const isSendingGamepadToUav = useRef(false)
    const stopDelayMonitor = useRef<(() => void) | null>(null);
    const [fpvLatency, setFpvLatency] = useState(0);
    const [voltage, setVoltage] = useState(0);
    const [vfrHud, setVfrHud] = useState<VfrHudMessage>();
    const [receiveTelemetry, setReceiveTelemetry] = useState(false);
    const [codecSupportedText, setCodecSupportedText] = useState<string | null>();
    const rtcId = "id" + Math.random().toString(5).slice(15);
    const mavParser = new MavlinkParser();

    const [disableP2P, setDisableP2P] = useState<boolean>(() => {
        return localStorage.getItem('fpv_disableP2P') === '1';
    });
    const [disableRelay, setDisableRelay] = useState<boolean>(() => {
        return localStorage.getItem('fpv_disableRelay') === '1';
    });

    const disableP2PRef = useRef(disableP2P);
    const disableRelayRef = useRef(disableRelay);

    function setP2PDisabled(state: boolean) {
        if (state === disableP2PRef.current) {
            return;
        }
        logger.debug(`P2P disabled: ${state}`);
        disableP2PRef.current = state;
        setDisableP2P(state);
    }

    function setRelayDisabled(state: boolean) {
        if (state === disableRelayRef.current) {
            return;
        }
        logger.debug(`Relay disabled: ${state}`);
        disableRelayRef.current = state;
        setDisableRelay(state);
    }

    useEffect(() => {
        disableP2PRef.current = disableP2P;
        localStorage.setItem('fpv_disableP2P', disableP2P ? '1' : '0');
    }, [disableP2P]);

    useEffect(() => {
        disableRelayRef.current = disableRelay;
        localStorage.setItem('fpv_disableRelay', disableRelay ? '1' : '0');
    }, [disableRelay]);

    useEffect(() => {
        axisOrderRef.current = axisOrder;
    }, [axisOrder]);

    useEffect(() => {
        axisReverceRef.current = axisReverse;
    }, [axisReverse]);

    // === загрузка ICE с сервера ===
    useEffect(() => {
        axios.get<IceConfig>('api/fpv/ice?imei=' + drone?.imei)
            .then(response => {
                setIce(response.data);
                logger.debug(response.data);
            })
            .catch((error) => { logger.error(error.response) });
    }, []);

    useEffect(() => {
        handlePlayClick();
    }, [ice]);

    useEffect(() => {
        const gamePadDisconnected = () => {
            const gamepad = gamepadRef.current[gamepadId];
            if (gamepad) {
                if (!gamepad.connected) {
                    setGamepadId(-1);
                    logger.debug("gamepad disconnected");
                }
            }
        }

        const checkGamepads = () => {
            const gamepads = navigator.getGamepads();
            logger.debug("gamepads count: " + gamepads.length)
            const gamepad = gamepads[gamepadId];

            if (gamepads) {
                gamepadRef.current = gamepads
            }
            if (gamepad) {
                logger.debug("🎮 Gamepad already connected:", gamepad);

                axios.post<FpvSettings>("api/fpv/getFpvSettings", {
                    imei: drone?.imei,
                    gamepadId: gamepad.id,
                })
                    .then((response) => {
                        if (response.data) {
                            setAxisOrder(response.data.rcOrder);
                            setAxisReverse(response.data.rcReverse);
                        }
                    })
                    .catch((error) => logger.error(error.response));
            }
        };

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = ""; // нужно для большинства браузеров
        };

        // проверка сразу
        checkGamepads();

        // плюс слушатель на будущее
        window.addEventListener("gamepadconnected", checkGamepads);
        window.addEventListener("gamepaddisconnected", gamePadDisconnected);
        window.addEventListener("beforeunload", handleBeforeUnload); //для перезагрузки страници
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
            window.removeEventListener("gamepadconnected", checkGamepads);
            window.removeEventListener("gamepaddisconnected", gamePadDisconnected);
        };
    }, [])

    useEffect(() => {
        let frameId: number;
        let lastSendTime = 0;
        const sendInterval = 10;

        const pollGamepad = (timestamp: number) => {
            const gamepads = navigator.getGamepads();
            if (gamepads) {
                gamepadRef.current = gamepads
            }
            const gamepad = gamepadRef.current[gamepadId];

            if (gamepad) {
                // === UI обновление ===
                const scaledAxesUI = axisOrderRef.current.map((i, it) => {
                    if (i < 0) { // i < 0 - buttons
                        const val = gamepad.buttons[i * -1 - 1];
                        const reverse = axisReverceRef.current[it];

                        return val.pressed
                            ? 100 - 100 * reverse
                            : 0 + 100 * reverse;
                    }

                    const val = gamepad.axes[i];
                    const reverse = axisReverceRef.current[it];

                    const result = Number.isFinite(val) ? Math.round(Math.abs(-reverse + ((val + 1) / 2)) * 100) : 0;
                    return result;
                });
                setAxesValues([
                    ...scaledAxesUI,
                    ...Array(Math.max(0, 10 - scaledAxesUI.length)).fill(0),
                ]);

                // === Отправка в dataChannel ===
                if (rcDataChannelRef.current?.readyState === "open" &&
                    timestamp - lastSendTime >= sendInterval) {
                    const scaledAxes = axisOrderRef.current.map((i, it) => {
                        if (i < 0) { // i < 0 - buttons
                            const val = gamepad.buttons[i * -1 - 1];
                            const reverse = axisReverceRef.current[it];

                            return val.pressed
                                ? 2000 - 1000 * reverse
                                : 1000 + 1000 * reverse;
                        }

                        const val = gamepad.axes[i];
                        const reverse = axisReverceRef.current[it];
                        return Number.isFinite(val)
                            ? Math.min(2000, Math.max(1000, Math.round(Math.abs(-reverse + ((val + 1) / 2)) * 1000 + 1000)))
                            : 0;
                    });

                    try {
                        if (isSendingGamepadToUav.current) {
                            rcDataChannelRef.current.send(new Uint16Array(scaledAxes).buffer);
                        }
                    } catch (err) {
                        console.error("DataChannel send error:", err);
                    }
                    lastSendTime = timestamp;
                }
            }

            frameId = requestAnimationFrame(pollGamepad);
        };

        frameId = requestAnimationFrame(pollGamepad);
        return () => cancelAnimationFrame(frameId);
    }, [gamepadId]);

    // === выход из FPV ===
    // Компонент размонтируется — гасим всё, что работает в фоне: иначе после
    // выхода остаются соединение с дроном, переподключение WebSocket и,
    // главное, отправка RC с геймпада. Берём ссылки, а не состояние: в момент
    // монтирования pc ещё null.
    useEffect(() => {
        return () => {
            closingRef.current = true;
            isSendingGamepadToUav.current = false;
            sendingPing.current = false;
            if (sendingWsRequestRef.current) clearInterval(sendingWsRequestRef.current);
            // Нужен последний запущенный монитор, а не тот, что был при монтировании.
            // eslint-disable-next-line react-hooks/exhaustive-deps
            stopDelayMonitor.current?.();

            const p = pcRef.current;
            if (p) {
                p.ontrack = null;
                p.ondatachannel = null;
                p.oniceconnectionstatechange = null;
                p.onicegatheringstatechange = null;
                p.onsignalingstatechange = null;
                p.onconnectionstatechange = null;
                p.getSenders().forEach(sender => sender.track?.stop());
                p.close();
                pcRef.current = null;
            }

            if (websocketRef.current) {
                websocketRef.current.close();
                websocketRef.current = null;
            }
            rcDataChannelRef.current = null;
            telemDataChannelRef.current = null;
            bitrateDataChannelRef.current = null;
        };
    }, []);

    // === при смене дрона ===
    useEffect(() => {
        if (drone === null) return;
        stop();
        setShowPlayButton(true);
        setWaitVideo(false);
    }, [drone]);

    const handlePlayClick = () => {
        if (checkAvalible(drone)) {
            createWs(false);
            setShowPlayButton(false);
            setWaitVideo(true);
        }
    };

    const setPear = (pear: RTCPeerConnection | null) => {
        pcRef.current = pear;
        setPc(pear);
    };

    // === PeerConnection ===
    function createPeerConnection(): RTCPeerConnection {
        if (!ice) throw new Error("ICE config not loaded");

        const iceServers = disableRelayRef.current
            ? ice.iceServers.filter(s => {
                const urls = Array.isArray(s.urls) ? s.urls : [s.urls];
                return !urls.some(u => /^turns?:/i.test(u));
            })
            : ice.iceServers;

        logger.debug("Original ICE servers:", ice.iceServers);
        logger.debug("ICE servers after filter:", iceServers);

        const pc = new RTCPeerConnection({
            iceServers,
            iceTransportPolicy: disableP2PRef.current ? 'relay' : 'all',
        });

        setPear(pc);

        pc.addEventListener('iceconnectionstatechange', () => {
            logger.debug('iceconnection -> ' + pc.iceConnectionState);
            //if (['disconnected', 'failed', 'closed'].includes(pc.iceConnectionState)) {
            if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
                setTimeout(() => {
                    getConnectionType(pc).then(ct => {
                        if (ct !== "unknown") setConnectionType(ct);
                    });
                }, 300);
            }
        });

        pc.addEventListener('connectionstatechange', () => {
            logger.debug('connectionstate -> ' + pc.connectionState);
            if (pc.connectionState === "connected") {
                getConnectionType(pc).then(ct => setConnectionType(ct));
            }

            if (['failed', 'closed'].includes(pc.connectionState)) {
                retryConnection(pc);
            }
        });

        pc.addEventListener('icegatheringstatechange', () =>
            logger.debug('icegathering -> ' + pc.iceGatheringState));

        pc.addEventListener('signalingstatechange', () =>
            logger.debug('signalling -> ' + pc.signalingState));

        pc.ondatachannel = (event) => {
            const dataChannel = event.channel;//todo: label filter
            logger.debug("Входящий DataChannel:", dataChannel.label);
            if (dataChannel.label == "joystick") {
                rcDataChannelRef.current = dataChannel;
            } else if (dataChannel.label == "telemetry") {

                telemDataChannelRef.current = dataChannel;
                mavParser.onMessage(MavMessage.BATTERY_STATUS, (batt_status) => {
                    setVoltage(batt_status.voltage);
                });
                mavParser.onMessage(MavMessage.VFR_HUD, (vfr_hud) => {
                    setVfrHud(vfr_hud);
                });
                dataChannel.onmessage = ({ data }) => {
                    mavParser.parseMavlinkV2(new Uint8Array(data));
                    if (!receiveTelemetry) {
                        setReceiveTelemetry(true);
                    }
                };
            } else if (dataChannel.label == "bitrate") {
                bitrateDataChannelRef.current = dataChannel;
            }

            dataChannel.onopen = () => logger.info("DataChannel открыт");
            dataChannel.onclose = () => {
                logger.warn(`DataChannel close ${dataChannel.label}`);
            };
            dataChannel.onerror = (err) => {
                logger.error(`Error DataChannel: ${dataChannel.label}`, err);
            };
        };

        pc.ontrack = (evt) => {
            if (!videoRef.current) return;

            const remoteDesc = pc.remoteDescription?.sdp;

            if (remoteDesc) {
                if (remoteDesc.includes('H265') || remoteDesc.includes('HEVC')) {
                    if (!isH265Supported()) {
                        setCodecSupportedText("Кодек H.265 не поддерживается браузером");
                    }
                }
            }

            /*const receiver  = pc.getReceivers().find(s => s.track?.kind === "video");
            if (receiver) {
                try {
                    const params: any = receiver.getParameters?.() ?? {};
                    params.degradationPreference = "maintain-framerate";
      
                    const rcv: any = receiver; // <-- TS не будет ругаться
                    if (typeof rcv.setParameters === "function") {
                        rcv.setParameters(params)
                            .then(() => logger.debug("set min jitter (receiver)"))
                            .catch((err: any) => logger.warn("receiver.setParameters error:", err));
                    } else {
                        logger.warn("receiver.setParameters не поддерживается");
                    }
                } catch (err) {
                    logger.warn("Ошибка применения receiver parameters:", err);
                }
            }*/

            videoRef.current.srcObject = evt.streams[0];
            videoRef.current.onloadedmetadata = () => {
                videoRef.current?.play().then(() => {
                    setShowPlayButton(false);
                    setWaitVideo(false);
                }).catch(console.warn);
            };
        };

        //stopDelayMonitor.current = startDelayMonitor(pc, "video", 0.5, 2000);

        return pc;
    }

    // === WebSocket ===
    function createWs(notSendRequest: boolean) {
        if (!ice || !drone) return;
        const ws = new WebSocket(`${ice.ws.replace("wstest", "connect")}?token=${ice.wsToken}`);
        websocketRef.current = ws;

        ws.onopen = () => {
            logger.debug('ws open');
            logger.debug('notSendRequest: ' + notSendRequest);
            if (!notSendRequest) {
                startSendingPing(ws);
                sendPing(ws);
            }
        };

        ws.onmessage = async (evt) => {
            if (typeof evt.data !== 'string') return;
            if (evt.data === "partner connect") {
                logger.debug("partner connect");
            } else {
                const message = JSON.parse(evt.data);
                if (message.type === "offer") {
                    logger.debug('offer-sdp: ' + message.sdp);
                    await handleOffer(message, ws);
                } else if (message.type === "ping") {
                    if (sendingPing.current) {
                        stopSendingPing();
                        sendRequest(ws);
                    }
                }
            }
        };

        ws.onclose = () => {
            console.warn("❌ WebSocket закрыт");
            if (sendingWsRequestRef.current) clearInterval(sendingWsRequestRef.current);
            if (closingRef.current) return;
            setTimeout(() => {
                if (closingRef.current) return;
                const isWebRTCRunning =
                    pcRef.current &&
                    pcRef.current.connectionState === 'connected' &&
                    pcRef.current.iceConnectionState === 'connected';

                if (isWebRTCRunning) {
                    createWs(true); // только WS
                } else {
                    createWs(false); // полный перезапуск
                }
            }, 3000);
        };

        ws.onerror = (err) => {
            console.error("⚠️ WebSocket ошибка:", err);
            ws.close();
        };
    }

    // === вспомогательные функции для WS ===
    function sendRequest(ws: WebSocket) {
        if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ id: rtcId, type: "request" }));
        }
    }

    function startSendingPing(ws: WebSocket) {
        sendingPing.current = true;
        setLoadingText("  Ищу в сети...");
        sendingWsRequestRef.current = setInterval(() => sendPing(ws), 3000);
    }

    function stopSendingPing() {
        setLoadingText("  Пытаюсь получить видео...");
        sendingPing.current = false;
        if (sendingWsRequestRef.current) clearInterval(sendingWsRequestRef.current);
    }

    function sendPing(ws: WebSocket) {
        if (!sendingPing.current) return;
        if (ws.readyState === WebSocket.OPEN) {
            logger.debug("send ping");
            ws.send(JSON.stringify({ id: rtcId, type: "ping" }));
        }
    }

    // === Offer/Answer ===
    async function handleOffer(offer: OfferMessage, ws: WebSocket) {
        const p = createPeerConnection();
        await p.setRemoteDescription(offer);
        setPear(p);
        await sendAnswer(p, ws);
    }

    async function sendAnswer(p: RTCPeerConnection, ws: WebSocket) {
        await p.setLocalDescription(await p.createAnswer());
        await waitGatheringComplete(p);
        const answer = p.localDescription;
        if (!answer) return;
        ws.send(JSON.stringify({ id: rtcId, type: answer.type, sdp: answer.sdp }));
    }

    async function waitGatheringComplete(p: RTCPeerConnection) {
        return new Promise<void>((resolve) => {
            if (p.iceGatheringState === 'complete') {
                resolve();
            } else {
                p.addEventListener('icegatheringstatechange', () => {
                    if (p.iceGatheringState === 'complete') resolve();
                });
            }
        });
    }

    // === reconnect ===
    function retryConnection(_: RTCPeerConnection) {
        if (reconnectingRef.current) return;
        reconnectingRef.current = true;
        const ws = websocketRef.current;

        stopPc();
        setTimeout(() => {
            logger.debug("reconnect...");
            if (ws) startSendingPing(ws);
            else logger.debug("reconnect error, !ws");
            setShowPlayButton(false);
            setWaitVideo(true);
            reconnectingRef.current = false;
        }, 1500);
    }

    // === остановка ===
    function stopPc() {
        if (pc) {
            logger.debug("stop pc");
            pc.ontrack = null;
            pc.ondatachannel = null;
            pc.oniceconnectionstatechange = null;
            pc.onicegatheringstatechange = null;
            pc.onsignalingstatechange = null;
            pc.onconnectionstatechange = null;

            pc.getSenders().forEach(sender => {
                if (sender.track) sender.track.stop();
            });

            pc.close();
            setPear(null);
        }
    }

    function stop() {
        stopPc();
        if (websocketRef.current) {
            websocketRef.current.close();
            websocketRef.current = null;
        }
        rcDataChannelRef.current = null;
        telemDataChannelRef.current = null;
        bitrateDataChannelRef.current = null;
    }

    async function getConnectionType(pc: RTCPeerConnection): Promise<"turn" | "p2p-lan" | "p2p" | "unknown"> {
        const stats = await pc.getStats();

        let selectedPairId: string | undefined;
        stats.forEach(r => {
            if (r.type === "transport" && r.selectedCandidatePairId) {
                selectedPairId = r.selectedCandidatePairId;
            }
        });

        let pair: any = selectedPairId ? stats.get(selectedPairId) : undefined;
        if (!pair) {
            stats.forEach(r => {
                if (!pair && r.type === "candidate-pair" && r.state === "succeeded") {
                    pair = r;
                }
            });
        }

        if (!pair) {
            return "unknown";
        }

        const local = stats.get(pair.localCandidateId);
        const remote = stats.get(pair.remoteCandidateId);
        if (!local || !remote) {
            return "unknown";
        }

        if (local.candidateType === "relay" || remote.candidateType === "relay") {
            return "turn";
        }
        if (local.candidateType === "host" && remote.candidateType === "host") {
            return "p2p-lan";
        }
        if (local.candidateType === "srflx" || remote.candidateType === "srflx" ||
            local.candidateType === "prflx" || remote.candidateType === "prflx") {
            return "p2p";
        }

        return "unknown";
    }

    function startDelayMonitor(pc: RTCPeerConnection, kind = "video", thresholdSec = 0.5, checkInterval = 2000): () => void {
        const receiver = pc.getReceivers().find(r => r.track?.kind === kind);
        /*if (!receiver) {
          logger.debug("no receivers"); 
          return () => {};;
        }*/

        let timer = setInterval(async () => {
            const receiver = pc.getReceivers().find(r => r.track?.kind === kind);
            if (receiver) {
                const stats = await receiver.getStats();

                stats.forEach(report => {
                    if (report.type === "inbound-rtp" && report.kind === kind) {
                        if (report.jitterBufferEmittedCount > 0) {
                            const avgDelay =
                                report.jitterBufferDelay / report.jitterBufferEmittedCount;

                            logger.debug("latency: " + avgDelay)
                            setFpvLatency(avgDelay)

                            if (avgDelay > thresholdSec) {
                                console.warn(
                                    `⚠️ Задержка ${avgDelay.toFixed(
                                        2
                                    )}с > ${thresholdSec}s → сбрасываем трек`
                                );

                                // Остановим трек, чтобы очистить буфер
                                //receiver.track.stop();

                                // Через паузу можно восстановить трек заново
                                // (если используешь ontrack, он подтянется автоматически)
                                setTimeout(() => {
                                    logger.debug("🎬 Готово, трек сброшен, ждём новый поток");
                                }, 200);
                            }
                        }
                    }

                });
            }
        }, checkInterval);

        return () => clearInterval(timer); // функция для остановки мониторинга
    }

    // === утилиты ===
    const checkAvalible = (d: Drone | null): boolean => {
        if (!d) return false;
        if (d.version < 2002) return false;
        return true;
    };

    const saveOrderOnServer = (rcOrder: number[], gamepad?: Gamepad | null, rcReverse?: number[]) => {
        logger.debug(gamepad?.id);
        if (gamepad) {
            axios.post<FpvSettings>("api/fpv/saveFpvSettings", {
                imei: drone?.imei,
                gamepadId: gamepad.id,
                rcOrder: rcOrder,
                rcReverse: rcReverse
            })
                .then((response) => {

                })
                .catch((error) => logger.error(error.response));
        }
    };

    function onOffGamepad() {
        isSendingGamepadToUav.current = !isSendingGamepadToUav.current;
    }

    function sendBitrate(available: number, incoming: number) {
        if (Number.isNaN(available)) {
            available = -1;
        }

        if (Number.isNaN(incoming)) {
            incoming = -1;
        }

        let data = { available, incoming }
        try {
            bitrateDataChannelRef?.current?.send(JSON.stringify(data))
        } catch (err) {
            logger.error("Send bitrate error", err);
        }
    }

    function isH265Supported(): boolean {
        const codecs = RTCRtpReceiver.getCapabilities('video')?.codecs ?? [];
        return codecs.some(c => /h265|hevc/i.test(c.mimeType));
    }

    function handleReconnect() {
        logger.debug("Manual reconnect requested");
        stop();
        setShowPlayButton(false);
        setWaitVideo(true);
        setTimeout(() => {
            handlePlayClick();
        }, 200);
    }

    function ClosePanels() {
        setP2PSettingsOpen(false);
        setSettingsOpen(false);
    }

    function OpenSettingsPanel() {
        ClosePanels();
        setP2PSettingsOpen(true);
    }

    function OpenGamepadSettingsPanel() {
        ClosePanels();
        setSettingsOpen(true);
    }

    // === JSX ===
    return (
        <>
            <TelemetryStat receiveTelemetry={receiveTelemetry} voltage={voltage} vfrHud={vfrHud} />
            <FpvStat connectionType={connectionType} pc={pcRef.current} onBitrate={sendBitrate} />
            {settingsOpen ?
                <></> :
                <div className='fpv-menu-btn gamepad' onClick={OpenGamepadSettingsPanel}>
                    <div className='on-gamepad-menu'>
                        {/*<Button variant="primary" onClick={onOffGamepad}>{isSendingGamepadToUav.current ? "ВЫКЛЮЧИТЬ" : "ВКЛЮЧИТЬ"}</Button>*/}
                        <img className='fpv-menu-btn-img' src='/image/astra/fpv/gamepad.svg' />
                        <div className={isSendingGamepadToUav.current ? "green_text" : "red_text"}>{isSendingGamepadToUav.current ? "ВКЛЮЧЕН" : "ВЫКЛЮЧЕН"}</div>
                    </div>
                </div>
            }
            {settingsOpen || p2pSettingsOpen ?
                <></> :
                <div
                    className='fpv-menu-btn exit'
                    onClick={onClose}
                    title='Выйти из FPV'
                >
                    <div className='on-gamepad-menu'>
                        <img className='fpv-menu-btn-img' src='/image/astra/fpv/close.svg' alt='' />
                        <div>ВЫХОД</div>
                    </div>
                </div>
            }
            {settingsOpen || p2pSettingsOpen ?
                <></> :
                <div
                    className='fpv-menu-btn settings'
                    onClick={OpenSettingsPanel}
                >
                    <div className='on-gamepad-menu'>
                        <img
                            className='fpv-menu-btn-img'
                            src='/image/astra/fpv/icon-settings.svg'
                        />
                        <div>НАСТРОЙКИ</div>
                    </div>
                </div>
            }
            <div
                className="fpv-menu-panel settings"
                style={{
                    transition: "transform 0.3s ease-in-out",
                    transform: p2pSettingsOpen ? "translateX(0)" : "translateX(-100%)",
                    overflowY: "auto"
                }}
            >
                <div className='fpv-menu-panel-label'>
                    <div>Настройки</div>
                    <div></div>
                    <img
                        className='fpv-menu-btn-img'
                        src='/image/astra/fpv/close.svg'
                        onClick={() => setP2PSettingsOpen(!p2pSettingsOpen)}
                    />
                </div>

                <div style={{ padding: '10px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <input
                            id="disable-p2p"
                            type="checkbox"
                            checked={disableP2P}
                            onChange={(e) => setP2PDisabled(e.target.checked)}
                            style={{ width: 16, height: 16 }}
                        />
                        <label htmlFor="disable-p2p" style={{ margin: 0, cursor: 'pointer' }}>
                            Блокировать P2P соединение
                        </label>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                        <input
                            id="disable-relay"
                            type="checkbox"
                            checked={disableRelay}
                            onChange={(e) => setRelayDisabled(e.target.checked)}
                            style={{ width: 16, height: 16 }}
                        />
                        <label htmlFor="disable-relay" style={{ margin: 0, cursor: 'pointer' }}>
                            Блокировать Relay соединение
                        </label>
                    </div>

                    {disableP2P && disableRelay && (
                        <div style={{ color: 'red', marginTop: 8 }}>
                            Оба способа отключены — соединение невозможно
                        </div>
                    )}
                </div>
                <div style={{ marginTop: 15 }}>
                    <Button variant="primary" onClick={handleReconnect}>
                        Применить и переподключиться
                    </Button>
                </div>
            </div>
            <div
                className="fpv-menu-panel gamepad"
                style={{
                    transition: "transform 0.3s ease-in-out",
                    transform: settingsOpen ? "translateX(0)" : "translateX(-100%)",
                    overflowY: "auto"
                }}>
                <div className='fpv-menu-panel-label'>
                    <Button variant="primary" onClick={onOffGamepad}>{isSendingGamepadToUav.current ? "ВЫКЛЮЧИТЬ" : "ВКЛЮЧИТЬ"}</Button>
                    <div></div>
                    <img className='fpv-menu-btn-img' src='/image/astra/fpv/close.svg' onClick={() => setSettingsOpen(!settingsOpen)} />
                </div>
                {Object.values(gamepadRef.current).filter((gp): gp is Gamepad => gp?.connected ?? false).length > 0 ? (
                    <select
                        value={gamepadId}
                        onChange={(e) => { setGamepadId(parseInt(e.target.value)) }}
                    >
                        <option key={-1} value={-1} disabled>
                            -- выберите геймпад --
                        </option>
                        {Object.values(gamepadRef.current)
                            .filter((gp): gp is Gamepad => gp?.connected ?? false)
                            .map((gp) => (
                                <option key={gp.index} value={gp.index}>
                                    {gp.id}
                                </option>
                            ))}
                    </select>
                ) : (
                    <div>
                        <div>Нет подключенных геймпадов</div>
                        <div>Пошевелите стиком, если подключили</div>
                    </div>
                )}
                <div className='fpv-menu-panel-rcs'>
                    <div className='fpv-menu-panel-rc'>
                        <div>RC</div>
                        <div>Gamepad</div>
                        <div>Value</div>
                        <div>Reverse</div>
                    </div>
                    {axisOrder.map((axis, idx) => (
                        <div className='fpv-menu-panel-rc' key={idx} style={{ marginBottom: 4 }}>
                            <div>{idx + 1}</div>
                            <Dropdown className='w-100'>
                                <Dropdown.Toggle className="w-100" variant="success" id="dropdown-basic" style={{ lineHeight: 1 }}>
                                    {axis >= 0 ? "Axis " + axis : "Button " + (axis * -1)}
                                </Dropdown.Toggle>

                                <Dropdown.Menu className='gamepad-dropdown-axis' >
                                    {gamepadRef.current[gamepadId]?.axes?.map((_, i) => (

                                        <Dropdown.Item onClick={() => {
                                            const newOrder = [...axisOrder];
                                            newOrder[idx] = i;
                                            setAxisOrder(newOrder);
                                            saveOrderOnServer(newOrder, gamepadRef.current[gamepadId], axisReverse);
                                        }
                                        }>Axis {i}</Dropdown.Item>
                                    ))}

                                    {gamepadRef.current[gamepadId]?.buttons?.slice(0, 6).map((_, i) => (
                                        <Dropdown.Item onClick={() => {
                                            const newOrder = [...axisOrder];
                                            newOrder[idx] = (i + 1) * -1;
                                            setAxisOrder(newOrder);
                                            saveOrderOnServer(newOrder, gamepadRef.current[gamepadId], axisReverse);
                                        }
                                        }>Button {i + 1}</Dropdown.Item>
                                    ))}

                                </Dropdown.Menu>
                            </Dropdown>
                            <ProgressBar striped variant="success" label={Number.isFinite(axesValues[idx]) ? axesValues[idx] : 0} now={Number.isFinite(axesValues[idx]) ? axesValues[idx] : 0} />
                            <input
                                type="checkbox"
                                checked={(axisReverse[idx] > 0 ? true : false)}
                                onChange={(e) => {
                                    const newAxisReverse = [...axisReverse];
                                    newAxisReverse[idx] = e.target.checked ? 1 : 0;
                                    setAxisReverse(newAxisReverse);
                                    saveOrderOnServer(axisOrder, gamepadRef.current[gamepadId], newAxisReverse);
                                }}
                                style={{ width: "20px", marginLeft: 10 }}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {checkAvalible(drone) &&
                <div
                    id="media"
                    ref={videoContainerRef}
                    className="fpv-video-container"
                    style={{ display: 'block' }}
                >
                    <video
                        ref={videoRef}
                        id="video"
                        autoPlay
                        playsInline
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                    {showPlayButton && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 10,
                                pointerEvents: 'none',
                            }}
                        >
                            <button
                                onClick={handlePlayClick}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    cursor: 'pointer',
                                    pointerEvents: 'auto',
                                }}
                            >
                                ▶ Connect
                            </button>
                        </div>
                    )}

                    {waitVideo && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 10,
                                pointerEvents: 'none',
                            }}
                        >
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                            <div>{loadingText}</div>
                        </div>
                    )}

                    {codecSupportedText && <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10,
                            pointerEvents: 'none',
                        }}
                    >
                        <div>{codecSupportedText}</div>
                    </div>}
                </div>
            }
        </>
    );
};

export default FpvRtc;
